import {
    AfterContentInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewContainerRef,
} from '@angular/core';
import {
    animationFrameScheduler,
    of,
    scheduled,
    Subject,
    Subscription,
} from 'rxjs';
import { auditTime, delay } from 'rxjs/operators';

import { takeUntilDestroy } from '../../../common/take-until-destroy.decorator';

import { Cell, Column, Row } from './experimental.grid';
import { ExperimentalNgGridComponent } from './experimental.ng.grid';
import {
    calculateMinTextWidth,
    getColumnCellEls,
    getExistingEntry,
    getHeaderCellEls,
} from './reusable-grid.function';
import {
    ExpandableRow,
    ExpandableRowEntry,
    ReusableCellContext,
    SelectType,
} from './reusable-grid.model';

@Component({
    selector: 'reusable-grid',
    template: '',
    host: {
        class: 'reusable-grid',
    },
})
export class ReusableGridComponent
    extends ExperimentalNgGridComponent
    implements OnInit, OnChanges, AfterContentInit, OnDestroy
{
    @Input() selectType: SelectType;
    @Output() selectRow: EventEmitter<Set<Row['id']>>;

    private readonly _selectedRows: Set<Row['id']>;

    private _inflateSubscriber: Subscription;
    private _rendererLayoutSubscriber: Subscription;
    private readonly _elDestroyedSubject: Subject<void>;
    private readonly _expandableRows: ExpandableRowEntry[];

    constructor(
        protected readonly _viewContainerRef: ViewContainerRef,
        protected _el: ElementRef,
    ) {
        super(_viewContainerRef);

        this.selectRow = new EventEmitter();
        this.selectType = 'multi';
        this._elDestroyedSubject = new Subject();
        this._selectedRows = new Set();
        this._expandableRows = [];
    }

    ngOnInit(): void {
        this.rendererOptions = {
            ...this.rendererOptions,
            autoFit: true,
            useStandardAutofit: true,
        };
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.rows != null) {
            this._formatRowsInput();
        }
        super.ngOnChanges(changes);
    }

    ngAfterContentInit(): void {
        this._inflateSubscriber = this.inflate.subscribe(() =>
            this._onInflate(),
        );

        super.ngAfterContentInit();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this._elDestroyedSubject.next();
        this.selectRow.complete();
        this._inflateSubscriber.unsubscribe();
        this._rendererLayoutSubscriber.unsubscribe();
    }

    private _formatRowsInput(): void {
        this.rows = this.rows.map((row) => this._formatRow(row));
    }

    private _formatRow(row: Row): Row {
        const rowCopy = { ...row };

        let isInExpandableRow = false;
        let isRowChecked = false;

        if (rowCopy.children != null && rowCopy.children.length > 0) {
            isInExpandableRow = true;
            const entry = this._saveExpandableRowEntry(
                rowCopy as ExpandableRow<any, any>,
            );

            rowCopy.children = entry.isExpanded
                ? rowCopy.children.map((child) => this._formatRow(child))
                : [];

            isRowChecked = entry.isExpanded;
        }

        rowCopy.cells = rowCopy.cells.map<Cell>((cell, index) => {
            const cellCopy = this._formatCell(cell, index, row, {
                isRowChecked,
                isInExpandableRow,
            });
            return cellCopy;
        });

        return rowCopy;
    }

    private _formatCell(
        cell: Cell,
        indexInRow: number,
        row: Row,
        opts: {
            isRowChecked?: boolean;
            isInExpandableRow: boolean;
        } = { isInExpandableRow: false },
    ): Cell {
        const cellCopy = { ...cell };
        const currentCellCtx = cellCopy.ctx;

        let checkboxVariant: 'radio' | 'default';
        if (indexInRow === 0) {
            checkboxVariant =
                this.selectType === 'single' ? 'radio' : 'default';
        }

        let checked = false;
        if (cellCopy.ctx.checked != null) {
            checked = cellCopy.ctx.checked;
        } else if (opts.isRowChecked != null) {
            checked = opts.isRowChecked;
        }

        const reusableCellCtx: ReusableCellContext = {
            checkboxVariant,
            isInExpandableRow: opts.isInExpandableRow,

            checked,
            clickRow: () => {
                this._expandRow(row.id);
                this._selectRow(row.id);
            },
        };

        this._addResizeSubj(reusableCellCtx);

        cellCopy.ctx = { ...currentCellCtx, ...reusableCellCtx };
        return cellCopy;
    }

    private _addResizeSubj(cellCtx: ReusableCellContext): void {
        if (this.renderer?.onResize != null) {
            cellCtx.resizeSubject = this.renderer.onResize;
        }
    }

    private _expandRow(rowId: Row['id']): void {
        const expandableRowEntry = getExistingEntry(
            this._expandableRows,
            rowId,
        ) as ExpandableRowEntry;

        if (expandableRowEntry != null) {
            const { row, isDirectMatch } = this._findRowById(this.rows, rowId);

            if (row != null && isDirectMatch) {
                expandableRowEntry.isExpanded = !expandableRowEntry.isExpanded;
            }

            if (expandableRowEntry.isExpanded) {
                row.children = expandableRowEntry.children;
            } else {
                row.children = [];
            }

            super._setRows();
            super._setSortDefs();
        }
    }

    private _findRowById(
        rows: Row[],
        rowId: string,
    ): { row: Row | undefined; isDirectMatch: boolean } {
        for (const row of rows) {
            if (row.id === rowId) {
                return { row, isDirectMatch: true };
            }
            if (row.children?.length > 0) {
                for (const child of row.children) {
                    if (child.id === rowId) {
                        return { row: child, isDirectMatch: false };
                    }
                }
            }
        }
        return { row: undefined, isDirectMatch: false };
    }

    private _selectRow(row: Row['id']): void {
        const rowId = row.includes('-') ? row.split('-').pop() : row;
        if (this.selectType !== null) {
            if (this._selectedRows.has(rowId)) {
                if (this.selectType === 'multi') {
                    this._selectedRows.delete(rowId);
                }

                this.selectRow.emit(this._selectedRows);
                return;
            }

            if (this.selectType === 'single') {
                this._selectedRows.clear();
            }
            this._selectedRows.add(rowId);
            this.selectRow.emit(this._selectedRows);
        }
    }

    private _saveExpandableRowEntry(
        expandableRow: ExpandableRow<any, any>,
    ): ExpandableRowEntry {
        const existingExpRowEntry = getExistingEntry(
            this._expandableRows,
            expandableRow.id,
        ) as ExpandableRowEntry;

        if (existingExpRowEntry != null) {
            existingExpRowEntry.children = [...expandableRow.children];
            return existingExpRowEntry;
        }

        const newEntry = {
            rowId: expandableRow.id,
            isExpanded: expandableRow.isExpanded,
            children: expandableRow.children,
        };

        this._expandableRows.push(newEntry);

        return newEntry;
    }

    private _onInflate(): void {
        this._addResizeSubjectToAllCells();
        this._subscribeToRendererLayout();
    }

    private _addResizeSubjectToAllCells(): void {
        this.rows.forEach((row) => {
            row.cells.forEach((cell) => this._addResizeSubj(cell.ctx));
            if (row.children != null) {
                row.children.forEach((childRow) =>
                    childRow.cells.forEach((childCell) =>
                        this._addResizeSubj(childCell.ctx),
                    ),
                );
            }
        });

        this._expandableRows.forEach((entry) => {
            entry.children.forEach((childRow) =>
                childRow.cells.forEach((childCell) =>
                    this._addResizeSubj(childCell.ctx),
                ),
            );
        });
    }

    private _subscribeToRendererLayout(): void {
        if (this._rendererLayoutSubscriber != null) {
            this._rendererLayoutSubscriber.unsubscribe();
        }

        this._rendererLayoutSubscriber = this.renderer.layoutt
            .pipe(auditTime(1, animationFrameScheduler), takeUntilDestroy(this))
            .subscribe(() => this._onRendererLayout());
    }

    private _onRendererLayout(): void {
        scheduled(of(null), animationFrameScheduler)
            .pipe(delay(1))
            .subscribe(() => {
                this._setGridMinHeight();
                void this._addMinColumnWidth();
            });
    }

    private _setGridMinHeight(): void {
        const height =
            this.renderer.gridNode.renderContext.element.scrollHeight;
        this._el.nativeElement.style.minHeight = `${height + 20}px`;
    }

    // TODO: determine if built-in grid functions may help
    // app/evaluation/grid/experimental.ng.grid.ts, ex: autoSizeColumns()
    // app/evaluation/landing/function/create-grid-data.function.ts, ex: cell hooks (willMeasure())
    private async _addMinColumnWidth(): Promise<void> {
        const headerCellEls = getHeaderCellEls(this.renderer);

        headerCellEls.forEach((headerCellEl: HTMLElement) => {
            void (async () => {
                const columnId = headerCellEl.getAttribute('column-id');
                if (headerCellEl.style.minWidth === '') {
                    const colDef: Column =
                        this.renderer.columnsInternal.columns.find(
                            (col) => col.id === columnId,
                        );

                    if (colDef.minWidth != null) {
                        headerCellEl.style.minWidth = `${colDef.minWidth}px`;
                    } else {
                        const minWidthCap = 50;
                        const minWidthCalc = await calculateMinTextWidth(
                            headerCellEl,
                            this._elDestroyedSubject,
                        );
                        const minWidth = Math.max(minWidthCap, minWidthCalc);
                        headerCellEl.style.minWidth = `${minWidth}px`;
                        colDef.minWidth = minWidth;
                    }
                }

                getColumnCellEls(
                    columnId,
                    this.renderer,
                    this.gridNode,
                ).forEach((contentCellEl: HTMLElement) => {
                    contentCellEl.style.minWidth = headerCellEl.style.minWidth;
                });
            })();
        });
    }
}
